import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Tooltip,
  Typography,
  message
} from 'antd'
import { QuestionCircleOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'

import qs from 'qs'
import { fetchPricingConfig, clearPricingConfigCache, getFormulaConstants } from '../utils/pricingConfig'
import { getManufacturedParts } from '../calculators/manufactured-parts'

const { Title, Text } = Typography

const FORMULA_FIELDS_KEYS = ['galvRate', 'overhead', 'margin', 'markup', 'fee105']

const FORMULA_FIELDS = [
  { key: 'galvRate', label: 'Galvanization Rate ($/lb)', step: 0.01, precision: 4 },
  { key: 'overhead', label: 'Overhead Factor (÷)', step: 0.01, precision: 4, tooltip: 'Divides cost — a lower number increases price, a higher number decreases price' },
  { key: 'margin', label: 'Margin Factor (÷)', step: 0.01, precision: 4, tooltip: 'Divides cost — a lower number increases price, a higher number decreases price' },
  { key: 'markup', label: 'Additional Markup (%)', step: 0.01, precision: 4, tooltip: 'Multiplied on top of the base price — increasing this increases the final price' },
  { key: 'fee105', label: 'Freight Fee ($)', step: 1, precision: 2 }
]

function calculateSuggestedPrice(inputData, buildingType, pricingConfig) {
  const parts = getManufacturedParts(inputData, buildingType, pricingConfig)
  let totalPartsCost = parts.reduce((a, b) => a + (b.cost || 0), 0)

  if (inputData['salt-fertilizer-building']) {
    const blackJack = parts.find(p => p.partNumber === '31555002')
    if (blackJack?.cost) totalPartsCost -= blackJack.cost
  }

  const sqft = Math.ceil((parseInt(inputData.buildingLength) * parseFloat(inputData.buildingWidthNumber)) || 0)
  const engineerDrawingsNeeded = inputData['engineer-sealed-drawings-needed-for-the-building-only']
  const { overhead, margin, markup, fee105 } = getFormulaConstants(pricingConfig)

  const basePrice = Math.ceil(
    Math.ceil((
      (totalPartsCost / overhead) +
      (engineerDrawingsNeeded ? 1 : 0) * (
        1250 +
        (sqft > 4500 ? 750 : 0) +
        (sqft > 8000 ? 500 : 0) +
        (sqft > 12000 ? 500 : 0) +
        (sqft > 15000 ? 500 : 0) +
        (sqft > 20000 ? 500 : 0) +
        (sqft > 25000 ? 1500 : 0)
      ) +
      fee105 * (
        (buildingType === 'advantage' ? 1 : 0) +
        (buildingType === 'integrity' ? Math.ceil(sqft / 3000) + (inputData.solidEndwallKit || 0) : 0) +
        (buildingType === 'integrity-cattle' ? Math.ceil(sqft / 3000) + (inputData.doorEndwallKit || 0) : 0)
      )
    ) / margin) / 10
  ) * 10

  return parseFloat(((basePrice + (inputData['salt-fertilizer-building'] ? 200 : 0)) * markup).toFixed(2))
}

function FormulaCell({ fieldDef, liveValue, draftValue, publishedValue, onSave }) {
  const { key, step, precision } = fieldDef
  const isMarkup = key === 'markup'
  const isCurrency = key === 'galvRate' || key === 'fee105'
  const toDisplay = v => v == null ? null : isMarkup ? parseFloat(((v - 1) * 100).toFixed(4)) : v
  const toStored = v => isMarkup ? parseFloat((v / 100 + 1).toFixed(6)) : v
  const fmt = v => {
    if (v == null) return '—'
    if (isMarkup) return `${v}%`
    if (isCurrency) return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
    return v
  }

  const currentVal = draftValue !== null && draftValue !== undefined ? draftValue : liveValue
  const [editing, setEditing] = useState(false)
  const [inputVal, setInputVal] = useState(toDisplay(currentVal))
  const [saving, setSaving] = useState(false)
  const priceChangeAnimated = React.useRef(false)

  const prevDisplayVal = toDisplay(publishedValue)
  const currentDisplayVal = toDisplay(currentVal)

  const PriceChange = ({ compareDisplayVal }) => {
    if (prevDisplayVal === null || compareDisplayVal === null || compareDisplayVal === undefined || compareDisplayVal === prevDisplayVal) return null
    const pct = ((compareDisplayVal - prevDisplayVal) / prevDisplayVal) * 100
    const sign = pct > 0 ? '+' : ''
    const isFirstTime = !priceChangeAnimated.current
    if (isFirstTime) priceChangeAnimated.current = true
    return (
      <div style={{
        marginTop: 6, fontSize: 11, overflow: 'hidden', maxHeight: 50, opacity: 1,
        animation: isFirstTime ? 'priceChangeIn 0.25s ease' : 'none',
      }}>
        <div style={{ borderTop: '1px solid #e8e8e8', marginBottom: 6 }} />
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#888' }}>Previous Value</span>
            <strong style={{ color: '#333', fontVariantNumeric: 'tabular-nums' }}>{fmt(prevDisplayVal)}</strong>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: pct > 0 ? '#23672A' : '#9C1937' }}>Change</span>
            <span style={{ color: pct > 0 ? '#23672A' : '#9C1937', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{sign}{pct.toFixed(2)}%</span>
          </div>
        </div>
      </div>
    )
  }

  const save = async () => {
    setSaving(true)
    try {
      await onSave(key, toStored(inputVal))
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setInputVal(toDisplay(currentVal))
    setEditing(false)
    if (draftValue === null || draftValue === undefined) {
      priceChangeAnimated.current = false
    }
  }

  if (editing) {
    return (
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <InputNumber
            size="small"
            value={inputVal}
            onChange={setInputVal}
            step={step}
            precision={precision}
            addonAfter={isMarkup ? '%' : undefined}
            style={{ width: 90 }}
            autoFocus
            onPressEnter={save}
          />
          <Button size="small" icon={<CheckOutlined />} onClick={save} loading={saving} type="primary" style={{ backgroundColor: '#7b2d3a', borderColor: '#7b2d3a' }} />
          <Button size="small" icon={<CloseOutlined />} onClick={cancel} style={{ backgroundColor: '#555', borderColor: '#555', color: '#fff' }} />
        </div>
        <PriceChange compareDisplayVal={inputVal} />
      </div>
    )
  }

  return (
    <Tooltip title="Click to edit">
      <div onClick={() => { setInputVal(toDisplay(currentVal)); setEditing(true) }} style={{ cursor: 'pointer' }}>
        <span style={{ fontWeight: 500 }}>{fmt(currentDisplayVal)}</span>
        <EditOutlined style={{ marginLeft: 6, fontSize: 11, color: '#aaa' }} />
        <PriceChange compareDisplayVal={currentDisplayVal} />
      </div>
    </Tooltip>
  )
}

function FormulaEditor({ onDraftChange, onLoad, reloadKey }) {
  const [draftFormula, setDraftFormula] = useState(null)
  const [liveFormula, setLiveFormula] = useState(null)
  const [snapshotFormula, setSnapshotFormula] = useState(null)

  const load = useCallback(async () => {
    const [configRes, draftRes, snapshotRes] = await Promise.all([
      axios.get('/api/pricing/config/advantage', { withCredentials: true }),
      axios.get('/api/pricing/formula/draft', { withCredentials: true }),
      axios.get('/api/pricing/formula/snapshot', { withCredentials: true }).catch(() => ({ data: null }))
    ])
    const live = configRes.data.formula
    const draft = draftRes.data
    setLiveFormula(live)
    setSnapshotFormula(snapshotRes.data?.formula ?? null)
    setDraftFormula(draft)
    onLoad && onLoad(draft)
  }, [onLoad])

  useEffect(() => { load() }, [load, reloadKey])

  const save = async (field, value) => {
    await axios.put('/api/pricing/formula', { field, value }, { withCredentials: true })
    clearPricingConfigCache()
    await load()
    onDraftChange && onDraftChange()
    message.success('Saved to draft')
  }

  if (!liveFormula) return null

  return (
    <Card size="small" title="Formula Constants (Global)" style={{ marginBottom: 16 }}>
      <Row gutter={[24, 20]}>
        {FORMULA_FIELDS.map(({ key, label, tooltip, ...rest }) => (
          <Col key={key} xs={24} sm={12} md={8}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
              {label}
              {tooltip && (
                <Tooltip title={tooltip}>
                  <QuestionCircleOutlined style={{ marginLeft: 5, fontSize: 11, color: '#aaa' }} />
                </Tooltip>
              )}
            </Text>
            <FormulaCell
              fieldDef={{ key, ...rest }}
              liveValue={liveFormula[key]}
              draftValue={draftFormula?.[key] ?? null}
              publishedValue={snapshotFormula?.[key] ?? liveFormula[key]}
              onSave={save}
            />
          </Col>
        ))}
      </Row>
    </Card>
  )
}

function DraftTester({ formulaDraft }) {
  const [buildingType, setBuildingType] = useState('advantage')
  const [estimates, setEstimates] = useState([])
  const [selectedEstimate, setSelectedEstimate] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  // Drafts from Advantage/Integrity that can optionally be layered in
  const [btDrafts, setBtDrafts] = useState({ advantage: null, integrity: null })
  const [includeDrafts, setIncludeDrafts] = useState({ advantage: false, integrity: false })

  // Fetch active BT drafts once on mount
  useEffect(() => {
    Promise.all([
      axios.get('/api/pricing/draft/advantage', { withCredentials: true }).then(r => r.data).catch(() => null),
      axios.get('/api/pricing/draft/integrity', { withCredentials: true }).then(r => r.data).catch(() => null)
    ]).then(([adv, int]) => setBtDrafts({ advantage: adv, integrity: int }))
  }, [])

  useEffect(() => {
    setSelectedEstimate(null)
    setResult(null)
    const types = buildingType === 'integrity' ? ['integrity', 'integrity-cattle'] : [buildingType]
    const query = qs.stringify({ buildingType: types, limit: 200 }, { arrayFormat: 'brackets' })
    axios.get(`/api/estimates?${query}`, { withCredentials: true })
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : []
        all.sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate))
        setEstimates(all)
      }).catch(err => console.error('DraftTester error:', err))
  }, [buildingType])

  useEffect(() => {
    if (selectedEstimate) runTest(selectedEstimate)
  }, [formulaDraft, includeDrafts])

  const runTest = async (estimate) => {
    if (!estimate) return
    setLoading(true)
    setResult(null)
    try {
      const estType = estimate.inputData?.buildingType
      const configType = estType === 'integrity-cattle' ? 'integrity' : estType
      const liveConfig = await fetchPricingConfig(configType)
      const draftConfig = JSON.parse(JSON.stringify(liveConfig))

      // Always apply formula draft
      if (formulaDraft) {
        FORMULA_FIELDS_KEYS.forEach(f => {
          if (formulaDraft[f] !== null && formulaDraft[f] !== undefined) {
            draftConfig.formula[f] = formulaDraft[f]
          }
        })
      }

      // Optionally layer in BT material/part drafts
      const btDraft = includeDrafts[configType] ? btDrafts[configType] : null
      if (btDraft?.materials?.length) {
        btDraft.materials.forEach(dm => {
          const m = draftConfig.materials.find(m => m.materialId === dm.materialId)
          if (m) {
            if (dm.pricePerPound !== null) m.pricePerPound = dm.pricePerPound
            if (dm.pricePerUnit !== null) m.pricePerUnit = dm.pricePerUnit
          }
        })
      }
      if (btDraft?.parts?.length) {
        btDraft.parts.forEach(dp => {
          const p = draftConfig.parts.find(p => p.partId === dp.partId)
          if (p) {
            if (dp.fastener !== null) p.fastener = dp.fastener
            if (dp.cover !== null) p.cover = dp.cover
          }
        })
      }

      const inputData = estimate.inputData || {}
      const livePrice = calculateSuggestedPrice(inputData, estType, liveConfig)
      const draftPrice = calculateSuggestedPrice(inputData, estType, draftConfig)
      setResult({ livePrice, draftPrice, diff: draftPrice - livePrice })
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fmt = v => v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
  const pct = result ? ((result.draftPrice - result.livePrice) / result.livePrice) * 100 : null
  const pctSign = pct > 0 ? '+' : ''

  const hasAdvDraft = !!btDrafts.advantage
  const hasIntDraft = !!btDrafts.integrity

  return (
    <Card size="small" title="Draft Price Tester" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Segmented
          value={buildingType}
          onChange={setBuildingType}
          options={[
            { label: 'Advantage', value: 'advantage' },
            { label: 'Integrity', value: 'integrity' }
          ]}
        />
        <Select
          showSearch
          placeholder="Estimate Name"
          style={{ width: 260 }}
          value={selectedEstimate?.id ?? undefined}
          loading={loading}
          onChange={id => {
            const est = estimates.find(x => x.id === id)
            setSelectedEstimate(est)
            runTest(est)
          }}
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          options={estimates.map(e => ({
            value: e.id,
            label: `${e.title || '(Untitled)'}${e.customerName ? ` — ${e.customerName}` : ''}`
          }))}
        />

        {(hasAdvDraft || hasIntDraft) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, borderLeft: '1px solid #e8e8e8', paddingLeft: 16, ...(selectedEstimate ? { borderRight: '1px solid #e8e8e8', paddingRight: 16 } : {}) }}>
            <span style={{ fontSize: 11, color: '#888' }}>Include price drafts</span>
            {hasAdvDraft && (
              <Checkbox
                checked={includeDrafts.advantage}
                onChange={e => setIncludeDrafts(prev => ({ ...prev, advantage: e.target.checked }))}
              >
                <span style={{ fontSize: 13 }}>Advantage</span>
              </Checkbox>
            )}
            {hasIntDraft && (
              <Checkbox
                checked={includeDrafts.integrity}
                onChange={e => setIncludeDrafts(prev => ({ ...prev, integrity: e.target.checked }))}
              >
                <span style={{ fontSize: 13 }}>Integrity</span>
              </Checkbox>
            )}
          </div>
        )}

        {result && (
          <div style={{ display: 'flex', gap: 40, animation: 'fadeSlideIn 0.2s ease' }}>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Previous Price</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#666667', fontVariantNumeric: 'tabular-nums' }}>{fmt(result.livePrice)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Draft Price</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#000A1B', fontVariantNumeric: 'tabular-nums' }}>{fmt(result.draftPrice)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: pct > 0 ? '#23672A' : '#9C1937', marginBottom: 2 }}>Change</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: pct > 0 ? '#23672A' : '#9C1937', fontVariantNumeric: 'tabular-nums' }}>
                {pctSign}{pct.toFixed(2)}%
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

export default function FormulaPage() {
  const [formulaDraft, setFormulaDraft] = useState(null)
  const [hasSnapshot, setHasSnapshot] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [publishing, setPublishing] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [rollingBack, setRollingBack] = useState(false)

  const triggerReload = () => setReloadKey(k => k + 1)

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get('/api/pricing/formula/snapshot', { withCredentials: true })
      setHasSnapshot(res.data.exists)
    } catch { setHasSnapshot(false) }
  }, [])

  useEffect(() => { fetchSnapshot() }, [fetchSnapshot])

  const publish = async () => {
    setPublishing(true)
    try {
      await axios.post('/api/pricing/formula/publish', {}, { withCredentials: true })
      clearPricingConfigCache()
      setFormulaDraft(null)
      triggerReload()
      fetchSnapshot()
      message.success('Formula published successfully')
    } catch {
      message.error('Failed to publish')
    } finally {
      setPublishing(false)
    }
  }

  const discard = () => {
    Modal.confirm({
      title: 'Discard formula draft?',
      content: 'All unpublished formula changes will be lost.',
      okText: 'Discard',
      okType: 'danger',
      onOk: async () => {
        setDiscarding(true)
        try {
          await axios.delete('/api/pricing/formula/draft', { withCredentials: true })
          clearPricingConfigCache()
          setFormulaDraft(null)
          triggerReload()
          message.success('Draft discarded')
        } catch {
          message.error('Failed to discard')
        } finally {
          setDiscarding(false)
        }
      }
    })
  }

  const rollback = () => {
    Modal.confirm({
      title: 'Roll back formula to last published state?',
      content: 'This will revert the live formula constants for all building types to the previous publish.',
      okText: 'Roll Back',
      okType: 'danger',
      onOk: async () => {
        setRollingBack(true)
        try {
          await axios.post('/api/pricing/formula/rollback', {}, { withCredentials: true })
          clearPricingConfigCache()
          setFormulaDraft(null)
          triggerReload()
          fetchSnapshot()
          message.success('Formula rolled back successfully')
        } catch {
          message.error('Failed to roll back')
        } finally {
          setRollingBack(false)
        }
      }
    })
  }

  const hasDraft = !!formulaDraft

  return (
    <>
      <Title level={2}>Formula</Title>

      <div id="parts-materials">
        {(hasDraft || hasSnapshot) && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={hasDraft ? 'Unpublished formula changes' : 'Last publish can be rolled back'}
            action={
              <Space>
                {hasDraft && <>
                  <Button size="small" type="primary" loading={publishing} onClick={publish}>
                    Publish
                  </Button>
                  <Button size="small" danger loading={discarding} onClick={discard}>
                    Discard
                  </Button>
                </>}
                {hasSnapshot && (
                  <Button size="small" loading={rollingBack} onClick={rollback}>
                    Roll Back
                  </Button>
                )}
              </Space>
            }
          />
        )}

        <FormulaEditor
          onDraftChange={triggerReload}
          onLoad={setFormulaDraft}
          reloadKey={reloadKey}
        />

        {hasDraft && (
          <DraftTester formulaDraft={formulaDraft} />
        )}
      </div>
    </>
  )
}
